import ReactGA from 'react-ga4';
import { analyticsLog, debugWarn } from './debug';

// Analytics configuration
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const isDev = process.env.NODE_ENV === 'development';

// Helper function to check if GA is initialized
export const isAnalyticsEnabled = () => {
  return !!(MEASUREMENT_ID);
};

// Initialize Google Analytics
export const initGA = () => {
  if (!MEASUREMENT_ID) {
    debugWarn('Google Analytics Measurement ID not found. Please set REACT_APP_GA_MEASUREMENT_ID in your environment variables.');
    return;
  }

  ReactGA.initialize(MEASUREMENT_ID, {
    testMode: isDev, // Enable test mode in development
    debug: isDev,    // Enable debug mode in development
  });

  analyticsLog(`Google Analytics initialized${isDev ? ' (test mode)' : ''}`);
};

// Track page views
export const trackPageView = (path, title) => {
  if (!MEASUREMENT_ID) return;
  
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: title
  });
  
  analyticsLog('Page view tracked:', { path, title });
};

// Track custom events
export const trackEvent = (action, category = 'general', label = null, value = null) => {
  if (!MEASUREMENT_ID) return;

  const eventData = {
    action,
    category,
    ...(label && { label }),
    ...(value && { value })
  };

  ReactGA.event(eventData);
  analyticsLog('Event tracked:', eventData);
};

// Testimony Form specific tracking
export const trackTestimonyEvent = (action, step = null, data = {}) => {
  if (!MEASUREMENT_ID) return;
  
  trackEvent(action, 'testimony_form', step, data.value);
  
  // Send additional data as custom parameters
  if (Object.keys(data).length > 0) {
    ReactGA.gtag('event', action, {
      event_category: 'testimony_form',
      event_label: step,
      custom_parameters: data
    });
  }
};

// Pre-defined testimony form events
export const testimonyEvents = {
  // Form interaction events
  formFocused: () => trackTestimonyEvent('form_focused'),
  formCancelled: (step) => trackTestimonyEvent('form_cancelled', `step_${step}`),
  
  // Step progression events
  stepStarted: (step) => trackTestimonyEvent('step_started', `step_${step}`),
  stepCompleted: (step) => trackTestimonyEvent('step_completed', `step_${step}`),
  stepBack: (fromStep, toStep) => trackTestimonyEvent('step_back', `${fromStep}_to_${toStep}`),
  
  // Step 1: Text input events
  textStarted: () => trackTestimonyEvent('text_input_started', 'step_1'),
  textCompleted: (charCount) => trackTestimonyEvent('text_input_completed', 'step_1', { char_count: charCount }),
  
  // Step 2: Audio recording events
  audioRecordingStarted: () => trackTestimonyEvent('audio_recording_started', 'step_2'),
  audioRecordingCompleted: (duration) => trackTestimonyEvent('audio_recording_completed', 'step_2', { duration }),
  audioRecordingCancelled: () => trackTestimonyEvent('audio_recording_cancelled', 'step_2'),
  audioPlayback: (action) => trackTestimonyEvent(`audio_playback_${action}`, 'step_2'), // play, pause, replay
  
  // Step 3: NGO selection events
  ngoSelectionStarted: () => trackTestimonyEvent('ngo_selection_started', 'step_3'),
  ngoSelected: (ngoId, selectionCount) => trackTestimonyEvent('ngo_selected', 'step_3', { ngo_id: ngoId, selection_count: selectionCount }),
  ngoDeselected: (ngoId, selectionCount) => trackTestimonyEvent('ngo_deselected', 'step_3', { ngo_id: ngoId, selection_count: selectionCount }),
  termsAccepted: () => trackTestimonyEvent('terms_accepted', 'step_3'),
  
  // Submission events
  submissionStarted: (data) => trackTestimonyEvent('submission_started', 'submit', {
    text_length: data.textLength,
    audio_duration: data.audioDuration,
    ngo_count: data.ngoCount
  }),
  submissionCompleted: (testimonyId) => trackTestimonyEvent('submission_completed', 'submit', { testimony_id: testimonyId }),
  submissionFailed: (error) => trackTestimonyEvent('submission_failed', 'submit', { error: error.message }),
  
  // Gallery interaction events
  testimonyViewed: (testimonyId) => trackEvent('testimony_viewed', 'gallery', testimonyId),
  testimonyAudioPlayed: (testimonyId) => trackEvent('testimony_audio_played', 'gallery', testimonyId),
  testimonyDonated: (testimonyId, amount) => trackEvent('testimony_donated', 'gallery', testimonyId, amount),
  
  // Navigation events
  heroCtaClicked: () => trackEvent('cta_clicked', 'hero', 'submit_confession'),
  galleryCtaClicked: () => trackEvent('cta_clicked', 'hero', 'view_gallery'),
  scrollToForm: () => trackEvent('scroll_to_form', 'navigation'),
  
  // Error tracking
  formError: (error, step) => trackTestimonyEvent('form_error', `step_${step}`, { error: error.message }),
  apiError: (endpoint, error) => trackEvent('api_error', 'technical', endpoint, { error: error.message })
};

// Wallet connection events
export const walletEvents = {
  connectionAttempted: (walletType) => trackEvent('wallet_connection_attempted', 'wallet', walletType),
  connectionSuccessful: (walletType, address) => trackEvent('wallet_connection_successful', 'wallet', walletType, { address }),
  connectionFailed: (walletType, error) => trackEvent('wallet_connection_failed', 'wallet', walletType, { error: error.message }),
  disconnected: (walletType) => trackEvent('wallet_disconnected', 'wallet', walletType)
};

// Performance tracking
export const performanceEvents = {
  loadTime: (componentName, loadTime) => trackEvent('load_time', 'performance', componentName, loadTime),
  apiResponseTime: (endpoint, responseTime) => trackEvent('api_response_time', 'performance', endpoint, responseTime)
};